import { motion } from "framer-motion";
import { Play, Pause, Loader2, ArrowUpRight, Film, Headphones } from "lucide-react";
import { Link } from "react-router-dom";
import { TRACKS } from "../../data/tracks";
import { MUSICAL_CATALYSTS } from "../../data/homeContent";
import { useAudio } from "../../context/AudioContext";

// 1.9 — Musical Catalysts / Listening Experience.
// Exactly three featured experiences. Media lives on-site (no external platform
// exits); the 60-second preview cap is enforced globally by AudioContext.
const Bars = ({ active }) => (
  <div className="flex items-end gap-[3px] h-6">
    {[0, 1, 2, 3, 4].map((b) => (
      <motion.span key={b} className="w-1 rounded-full bg-[#C9A24B]/80"
        animate={active ? { height: [6, 20, 10, 24, 8][b] } : { height: 5 }}
        transition={{ repeat: active ? Infinity : 0, repeatType: "reverse", duration: 0.5 + b * 0.12 }} />
    ))}
  </div>
);

export const MusicalCatalysts = () => {
  const { playTrack, currentId, isPlaying, loading } = useAudio();
  const [featured, second, third] = MUSICAL_CATALYSTS.experiences;

  const featTrack = TRACKS.find((t) => t.id === featured.trackId);
  const fActive = featured.trackId != null && currentId === featured.trackId;
  const fPlaying = fActive && isPlaying;

  return (
    <section id="experience" data-testid="musical-catalysts" className="relative py-24 md:py-32 grain overflow-hidden">
      <div className="line-weave" />
      <div className="aurora-weave slow" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[50rem] h-[50rem] radial-glow blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-6xl mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-12">
          <h2 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-cream">
            Don't Just Read <span className="text-gold">About It.</span><br />Feel It.
          </h2>
          <p className="text-base md:text-lg text-cream/60 max-w-xs md:text-right">
            {MUSICAL_CATALYSTS.sub}
          </p>
        </div>

        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-6">
          {/* Featured experience — Gratitude */}
          <motion.div
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="group relative rounded-3xl overflow-hidden ring-gold-frame min-h-[26rem] flex flex-col justify-end"
          >
            {featTrack && (
              <img src={featTrack.cover} alt={featured.title} loading="lazy"
                className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-85 group-hover:scale-[1.03] transition-all duration-700"
                onError={(e) => { e.currentTarget.style.display = "none"; }} />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#05070C] via-[#05070C]/55 to-transparent" />
            <div className="absolute top-5 left-5 flex items-center gap-2">
              <span className="px-3 py-1 rounded-full text-[11px] tracking-[0.2em] uppercase bg-black/40 backdrop-blur text-[#E7CC86]">{featured.badge}</span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] tracking-[0.16em] uppercase bg-black/40 backdrop-blur text-cream/70">
                <Film size={12} /> Film
              </span>
            </div>
            <div className="relative z-10 p-7">
              <button
                data-testid={`catalyst-play-${featured.id}`}
                onClick={() => featTrack && playTrack(featured.trackId)}
                aria-label={fPlaying ? `Pause ${featured.title}` : `Play ${featured.title}`}
                className="flex items-center gap-4 mb-4 text-left"
              >
                <span className="w-14 h-14 rounded-full btn-gold flex items-center justify-center shrink-0 animate-pulse-glow">
                  {!featTrack ? null : loading && fActive ? <Loader2 size={22} className="animate-spin text-[#14100A]" />
                    : fPlaying ? <Pause size={22} className="text-[#14100A]" fill="#14100A" />
                    : <Play size={22} className="ml-0.5 text-[#14100A]" fill="#14100A" />}
                </span>
                <Bars active={fPlaying} />
              </button>
              <h3 className={`font-display text-3xl leading-tight ${fActive ? "text-[#E7CC86]" : "text-cream"}`}>{featured.title}</h3>
              <p className="text-cream/55 text-sm mt-1">{featured.feat}</p>
              <p className="text-cream/80 text-[15px] mt-4 max-w-md leading-relaxed">{featured.descriptor}</p>
            </div>
          </motion.div>

          {/* Supporting experiences */}
          <div className="flex flex-col gap-3">
            {[second, third].map((x) => {
              const track = TRACKS.find((t) => t.id === x.trackId);
              const active = x.trackId != null && currentId === x.trackId;
              const playingThis = active && isPlaying;
              const playable = !x.mediaPending && track;
              return (
                <motion.div
                  key={x.id}
                  data-testid={`catalyst-${x.id}`}
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  className="flex-1 glass rounded-2xl p-4 flex items-center gap-4 transition-all group"
                >
                  <button
                    data-testid={`catalyst-play-${x.id}`}
                    onClick={() => playable && playTrack(x.trackId)}
                    disabled={!playable}
                    aria-label={playingThis ? `Pause ${x.title}` : playable ? `Play ${x.title}` : `${x.title} — preview coming soon`}
                    className="relative w-16 h-16 shrink-0 rounded-xl overflow-hidden bg-gradient-to-br from-[#12203a] to-[#05070C]"
                  >
                    {track && (
                      <img src={track.cover} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    )}
                    <span className="absolute inset-0 flex items-center justify-center bg-black/35 group-hover:bg-black/25 transition-colors">
                      {!playable ? <Film size={18} className="text-cream/45" />
                        : loading && active ? <Loader2 size={18} className="animate-spin text-[#E7CC86]" />
                        : playingThis ? <Pause size={20} className="text-[#E7CC86]" fill="#E7CC86" />
                        : <Play size={20} className="ml-0.5 text-[#E7CC86]" fill="#E7CC86" />}
                    </span>
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className={`font-display text-lg leading-tight ${active ? "text-[#E7CC86]" : "text-cream"}`}>{x.title}</p>
                    <p className="text-[13px] text-cream/55 mt-0.5">{x.feat}</p>
                    <p className="text-[13px] text-cream/65 mt-1 leading-snug">{x.descriptor}</p>
                    <p className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] tracking-[0.16em] uppercase text-cream/45">
                      {x.mediaPending ? (<><Film size={12} /> Full film arriving soon</>) : (<><Headphones size={12} /> Listening Experience</>)}
                    </p>
                  </div>
                  <div className="shrink-0">
                    {playable ? <Bars active={playingThis} /> : null}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        <div className="text-center mt-12">
          <Link to="/listen" data-testid="experience-listen-link" className="btn-gold inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold tracking-wide">
            {MUSICAL_CATALYSTS.cta} <ArrowUpRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
};
